import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { marketplaceService } from '../../services/marketplaceService';
import { favoriteService } from '../../services/favoriteService';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import MarketplaceFilters from './components/MarketplaceFilters';
import ListingCard from './components/ListingCard';
import CreateListingModal from './components/CreateListingModal';
import MarketplaceStats from './components/MarketplaceStats';
import EmptyState from './components/EmptyState';
import LoadingSpinner from '../../components/LoadingSpinner';

const UserMarketplace = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editListing, setEditListing] = useState(null);
  const [wishlistedItems, setWishlistedItems] = useState([]);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    condition: '',
    location: '',
    minPrice: '',
    maxPrice: '',
    nearMe: false
  });

  useEffect(() => {
    loadWishlistedItems();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params?.get('action') === 'create') {
      setEditListing(null);
      setIsCreateModalOpen(true);
      navigate('/user-marketplace', { replace: true });
    } else if (params?.get('edit')) {
      const editId = params?.get('edit');
      // Load the listing data for editing
      marketplaceService?.getItemById(editId)?.then(data => {
        if (data) {
          setEditListing(data);
          setIsCreateModalOpen(true);
        }
      })?.catch(err => console.error('Failed to load listing for edit:', err));
      navigate('/user-marketplace', { replace: true });
    }
  }, [location?.search]);

  useEffect(() => {
    loadMarketplaceItems();
  }, [filters]);

  const loadWishlistedItems = async () => {
    try {
      const favorites = await favoriteService?.getMyFavorites();
      const marketplaceFavorites = favorites
        ?.filter(fav => fav?.itemType === 'marketplace')
        ?.map(fav => fav?.id);
      setWishlistedItems(marketplaceFavorites || []);
    } catch (err) {
      console.error('Failed to load wishlist:', err);
    }
  };

  const loadMarketplaceItems = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await marketplaceService?.getItems(filters);
      setListings(data);
    } catch (err) {
      console.error('Failed to load marketplace items:', err);
      setError(err?.message || 'İlanlar yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const categoryOptions = [
    { value: 'boards', label: 'SUP Tahtaları' },
    { value: 'paddles', label: 'Kürekler' },
    { value: 'wetsuits', label: 'Dalış Kıyafetleri' },
    { value: 'accessories', label: 'Aksesuarlar' },
    { value: 'safety', label: 'Güvenlik Ekipmanları' }
  ];

  const conditionOptions = [
    { value: 'new', label: 'Sıfır' },
    { value: 'like-new', label: 'Az Kullanılmış' },
    { value: 'good', label: 'İyi Durumda' },
    { value: 'used', label: 'Kullanılmış' }
  ];

  const locationOptions = [
    { value: 'istanbul', label: 'İstanbul' },
    { value: 'izmir', label: 'İzmir' },
    { value: 'antalya', label: 'Antalya' },
    { value: 'mugla', label: 'Muğla' },
    { value: 'aydin', label: 'Aydın' },
    { value: 'canakkale', label: 'Çanakkale' }
  ];

  const priceRanges = [
    { value: '0-1000', label: '0 - 1.000 TL' },
    { value: '1000-5000', label: '1.000 - 5.000 TL' },
    { value: '5000-10000', label: '5.000 - 10.000 TL' },
    { value: '10000-20000', label: '10.000 - 20.000 TL' },
    { value: '20000+', label: '20.000 TL +' }
  ];

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      category: '',
      condition: '',
      location: '',
      minPrice: '',
      maxPrice: '',
      nearMe: false
    });
  };

  const handleContact = (listing) => {
    navigate('/messaging-center', { state: { recipientId: listing?.id, recipientName: listing?.sellerName } });
  };

  const handleWishlist = async (listingId) => {
    try {
      const isFavorited = await favoriteService?.toggleFavorite(listingId, 'marketplace');
      
      if (isFavorited) {
        setWishlistedItems([...wishlistedItems, listingId]);
      } else {
        setWishlistedItems(wishlistedItems?.filter(id => id !== listingId));
      }
    } catch (err) {
      console.error('Failed to toggle wishlist:', err);
    }
  };

  const handleCreateListing = async (formData) => {
    setError('');

    const imageUrls = Array.isArray(formData?.imageUrls)
      ? formData?.imageUrls?.slice(0, 5)
      : formData?.imageUrls?.split(',')?.map(url => url?.trim())?.filter(url => url?.length > 0)?.slice(0, 5);

    if (!imageUrls || imageUrls?.length === 0) {
      throw new Error('En az bir fotoğraf gereklidir');
    }

    if (formData?.editId) {
      // Edit mode: UPDATE existing listing
      await marketplaceService?.updateItem(formData?.editId, formData, imageUrls);
    } else {
      // Create mode: INSERT new listing
      await marketplaceService?.createItem(formData, imageUrls);
    }

    // Success: close modal and refresh listings
    setIsCreateModalOpen(false);
    setEditListing(null);
    loadMarketplaceItems();
  };

  const handleMarkAsSold = async (itemId) => {
    try {
      setError('');
      await marketplaceService?.markAsSold(itemId);
      loadMarketplaceItems(); // Reload items after update
    } catch (err) {
      console.error('Failed to mark as sold:', err);
      setError(err?.message || 'İlan güncellenirken bir hata oluştu');
    }
  };

  const handleDeleteListing = async (itemId) => {
    if (!window.confirm('Bu ilanı silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      setError('');
      await marketplaceService?.deleteItem(itemId);
      loadMarketplaceItems(); // Reload items after deletion
    } catch (err) {
      console.error('Failed to delete listing:', err);
      setError(err?.message || 'İlan silinirken bir hata oluştu');
    }
  };

  const marketplaceStats = {
    activeListings: listings?.length,
    totalSellers: new Set(listings.map(item => item.seller_id))?.size,
    newThisWeek: listings?.filter(item => {
      const createdDate = new Date(item.created_at);
      const weekAgo = new Date();
      weekAgo?.setDate(weekAgo?.getDate() - 7);
      return createdDate >= weekAgo;
    })?.length,
    activeChats: 156
  };

  return (
    <>
      <Header />
      <div className="main-content bg-background min-h-screen">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 mb-6 md:mb-8">
            <div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-2">
                Kullanıcı Pazarı
              </h1>
              <p className="text-sm md:text-base text-muted-foreground">
                Topluluk içinde güvenli alışveriş yapın
              </p>
            </div>
            <Button
              variant="default"
              size="lg"
              iconName="Plus"
              iconPosition="left"
              onClick={() => setIsCreateModalOpen(true)}>

              İlan Oluştur
            </Button>
          </div>

          {error && (
            <div className="bg-error/10 border border-error/20 rounded-lg p-4 mb-6 flex items-start gap-3">
              <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-error font-medium">{error}</p>
              </div>
            </div>
          )}

          <MarketplaceStats stats={marketplaceStats} />

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mt-6 md:mt-8">
            <div className="lg:col-span-1">
              <MarketplaceFilters
                filters={filters}
                onFilterChange={handleFilterChange}
                onClearFilters={handleClearFilters}
                locationOptions={locationOptions}
                categoryOptions={categoryOptions}
                conditionOptions={conditionOptions}
                priceRanges={priceRanges}
              />
            </div>

            <div className="lg:col-span-3">
              <div className="flex items-center justify-between mb-4 md:mb-6">
                <p className="text-sm md:text-base text-muted-foreground">
                  <span className="font-semibold text-foreground">{listings?.length}</span> ilan bulundu
                </p>
                <button className="flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-muted transition-smooth text-sm font-medium">
                  <Icon name="ArrowUpDown" size={16} />
                  Sırala
                </button>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <LoadingSpinner />
                </div>
              ) : listings?.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                  {listings?.map((listing) => {
                    const isOwner = user?.id === listing?.seller_id;
                    
                    return (
                      <ListingCard
                        key={listing?.id}
                        listing={listing}
                        onContact={handleContact}
                        onWishlist={handleWishlist}
                        isWishlisted={wishlistedItems?.includes(listing?.id)}
                        isOwner={isOwner}
                        onMarkAsSold={isOwner ? () => handleMarkAsSold(listing?.id) : null}
                        onDelete={isOwner ? () => handleDeleteListing(listing?.id) : null}
                      />
                    );
                  })}
                </div>
              ) : (
              <EmptyState onCreateListing={() => setIsCreateModalOpen(true)} />
              )}
            </div>
          </div>
        </div>
      </div>
      <CreateListingModal
        isOpen={isCreateModalOpen}
        onClose={() => { setIsCreateModalOpen(false); setEditListing(null); }}
        onSubmit={handleCreateListing}
        categoryOptions={categoryOptions}
        conditionOptions={conditionOptions}
        editListing={editListing}
      />
    </>
  );

};

export default UserMarketplace;